import Box from '@mui/material/Box';
import img from 'components/@auth/images/img.jpg';
import { isPreviewBuild } from 'components/common/previewEnv';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { apiUrl } from 'services/apiBaseUrl';
import { useMe } from 'store/app/auth/action';
import authConfigActions from 'store/app/authConfig/action';
import authUser from 'store/selectors/authUser';
import meStatus from 'store/selectors/meStatus';
import getSubdomain from 'utils/getSubdomain';
import Logo from './Logo';
import { PasskeyEnrollmentChromeContext } from './passkeyEnrollmentChrome';

const isLogin = pathname => pathname.toString() === '/auth/login';
const isPasskeyEnrollment = pathname => pathname.toString() === '/auth/login/passkey-enrollment';

// Backend validation pattern: /[A-Za-z0-9-]{2,20}/
const isValidSubdomain = subdomain => {
  if (!subdomain || typeof subdomain !== 'string') return false;
  return /^[A-Za-z0-9-]{2,20}$/.test(subdomain);
};

const AuthLayout = () => {
  const token = useSelector(state => state.auth?.token);
  const user = useSelector(authUser);
  const { isLoading, isUninitialized, isError, isFetching } = useSelector(meStatus);
  useMe();
  const subdomain = getSubdomain();
  const onBoardingImage = isValidSubdomain(subdomain)
    ? apiUrl(`/v1/company/${subdomain}/logo`)
    : img;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isBackVisible, setBackVisible] = useState(!isLogin(location.pathname));
  const [passkeyInviteVisible, setPasskeyInviteVisible] = useState(false);
  const onPasskeyEnrollment = isPasskeyEnrollment(location.pathname);

  useEffect(() => {
    // One-shot post-login target from the unauthenticated redirect (AuthGate). In-memory only;
    // handleLogin validates the host before following it.
    const returnUrl = new URLSearchParams(location.search).get('returnUrl');
    if (returnUrl) {
      dispatch(authConfigActions.setReturnURL(returnUrl));
    }
  }, [dispatch, location.search]);

  useEffect(() => {
    // Let users sign in on preview even when redux still has a stale token.
    if (isPreviewBuild()) {
      return;
    }
    // Token alone is not enough — AuthGate bounces guest sessions back to login.
    // Wait for /me to resolve so we do not oscillate login ↔ home without a 401.
    if (
      isPasskeyEnrollment(location.pathname) ||
      !token ||
      isUninitialized ||
      isLoading ||
      isFetching ||
      isError ||
      !user
    ) {
      return;
    }
    navigate('/home/messages');
  }, [location.pathname, navigate, token, user, isLoading, isUninitialized, isError, isFetching]);

  useEffect(() => {
    setBackVisible(!isLogin(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    if (!onPasskeyEnrollment) {
      setPasskeyInviteVisible(false);
    }
  }, [onPasskeyEnrollment]);

  return (
    <PasskeyEnrollmentChromeContext.Provider value={setPasskeyInviteVisible}>
      <Box
        component="section"
        sx={{
          display: onPasskeyEnrollment && !passkeyInviteVisible ? 'none' : 'flex',
          height: '100%',
        }}
      >
        <Box
          sx={{
            width: 500,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            padding: '0 30px',
            maxHeight: '100vh',
          }}
        >
          <Logo isBackVisible={isBackVisible} />
          <Outlet />
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            backgroundImage: `url(${onBoardingImage})`,
            backgroundSize: 'cover',
          }}
        />
      </Box>
    </PasskeyEnrollmentChromeContext.Provider>
  );
};

export default AuthLayout;
